import React, { useState, useCallback, useMemo } from 'react';
import {
  Grid, TextField, MenuItem, Autocomplete, Chip, Box, Typography, Rating,
  Tabs, Tab, Card, CardContent, Stack, Paper, Divider, FormControlLabel, Checkbox
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useSettings } from '../../contexts/SettingsContext';

const QuickEditSection = React.memo(({ values, onChange, handleStatusChange, tagsToUse }) => (
  <Grid container spacing={2}>
    <Grid item xs={12}>
      <Card variant="outlined" sx={{ bgcolor: 'action.hover', mb: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <Typography variant="subtitle2" gutterBottom color="primary" fontWeight="bold">
            🎯 Quick Status Update
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {["ANALYSIS", "OPEN", "WIN", "LOSS", "BREAKEVEN"].map((status) => (
              <Chip
                key={status}
                label={status}
                onClick={() => handleStatusChange(status)}
                color={values.status === status ? 'primary' : 'default'}
                variant={values.status === status ? 'filled' : 'outlined'}
                sx={{
                  cursor: 'pointer',
                  fontWeight: values.status === status ? 'bold' : 'normal',
                  '&:hover': { bgcolor: 'primary.light', color: 'white' }
                }}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Grid>

    <Grid item xs={12} sm={6}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" fontWeight="bold">💰 P&L Amount</Typography>
          <Chip
            label={values.manualPnL ? "Manual" : "Auto"}
            size="small"
            color={values.manualPnL ? "warning" : "primary"}
            onClick={() => onChange('manualPnL', !values.manualPnL)}
            sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
          />
        </Box>
        <TextField
          fullWidth
          value={values.profitLoss || ''}
          onChange={(e) => onChange('profitLoss', e.target.value)}
          disabled={!values.manualPnL}
          placeholder={values.manualPnL ? "Enter P&L amount" : "Auto-calculated from R:R"}
          InputProps={{
            startAdornment: values.profitLoss && parseFloat(values.profitLoss) >= 0 ?
              <TrendingUpIcon color="success" sx={{ mr: 1 }} /> :
              <TrendingDownIcon color="error" sx={{ mr: 1 }} />
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              color: values.profitLoss && parseFloat(values.profitLoss) >= 0 ? 'success.main' : 'error.main',
              bgcolor: values.manualPnL ? 'background.paper' : 'action.disabledBackground'
            }
          }}
        />
      </Box>
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        label="📊 Exit Price"
        type="number"
        fullWidth
        value={values.exitPrice || ''}
        onChange={(e) => onChange('exitPrice', e.target.value)}
      />
    </Grid>

    <Grid item xs={12}>
      <TextField
        label="💡 Trade Lesson"
        fullWidth
        multiline
        rows={2}
        value={values.notes || ''}
        onChange={(e) => onChange('notes', e.target.value)}
        placeholder="What did you learn? Any mistakes to avoid?"
        sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'action.hover' } }}
      />
    </Grid>

    <Grid item xs={12} sm={6}>
      <Box>
        <Typography component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>⭐ Trade Rating</Typography>
        <Rating name="trade-rating" value={values.rating || 3} onChange={(e, newVal) => onChange('rating', newVal)} size="large" />
      </Box>
    </Grid>

    <Grid item xs={12}>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>🏷️ Tags</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, minHeight: 40, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          {(Array.isArray(values.tags) ? values.tags : typeof values.tags === 'string' ? values.tags.split(',').map(tag => tag.trim()) : []).map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              onDelete={() => {
                const currentTags = Array.isArray(values.tags) ? values.tags : typeof values.tags === 'string' ? values.tags.split(',').map(t => t.trim()) : [];
                onChange('tags', currentTags.filter((_, i) => i !== index));
              }}
              size="small"
              color="primary"
              variant="filled"
              sx={{ borderRadius: '16px', fontWeight: 'medium' }}
            />
          ))}
        </Box>
        <Autocomplete
          freeSolo
          options={tagsToUse}
          value={''}
          onChange={(event, newValue) => {
            if (newValue) {
              const currentTags = Array.isArray(values.tags) ? values.tags : typeof values.tags === 'string' ? values.tags.split(',').map(t => t.trim()) : [];
              if (!currentTags.includes(newValue)) {
                onChange('tags', [...currentTags, newValue]);
              }
            }
          }}
          renderInput={(params) => <TextField {...params} placeholder="Type and press Enter to add tags" size="small" />}
        />
      </Box>
    </Grid>
  </Grid>
));
const DetailedSection = React.memo(({ values, onChange, handleStatusChange, strategies, trades, metrics, settings, tagsToUse }) => (
  <Grid container spacing={2}>
    {/* Group 1: Basic Trade Information */}
    <Grid item xs={12}>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
          📊 Basic Trade Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Category"
              fullWidth
              value={values.category || 'stock'}
              onChange={(e) => onChange('category', e.target.value)}
              size="small"
            >
              <MenuItem value="stock">Stock</MenuItem>
              <MenuItem value="crypto">Crypto</MenuItem>
              <MenuItem value="forex">Forex</MenuItem>
              <MenuItem value="derivative">Derivative</MenuItem>
              <MenuItem value="commodity">Commodity</MenuItem>
              <MenuItem value="index">Index</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Symbol"
              fullWidth
              value={values.symbol || ''}
              onChange={(e) => onChange('symbol', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              freeSolo
              options={[...new Set((trades || []).map(t => t.exchange).filter(Boolean))]}
              value={values.exchange || ''}
              onChange={(e, newValue) => onChange('exchange', newValue || '')}
              onInputChange={(e, newInputValue) => onChange('exchange', newInputValue || '')}
              renderInput={(params) => <TextField {...params} label="Exchange" fullWidth size="small" />}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Direction"
              fullWidth
              value={values.direction || 'LONG'}
              onChange={(e) => onChange('direction', e.target.value)}
              size="small"
            >
              <MenuItem value="LONG">Long</MenuItem>
              <MenuItem value="SHORT">Short</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              label="Status"
              fullWidth
              value={values.status || 'ANALYSIS'}
              onChange={(e) => handleStatusChange(e.target.value)}
              size="small"
            >
              <MenuItem value="ANALYSIS">Analysis</MenuItem>
              <MenuItem value="OPEN">Open</MenuItem>
              <MenuItem value="WIN">Win</MenuItem>
              <MenuItem value="LOSS">Loss</MenuItem>
              <MenuItem value="BREAKEVEN">Breakeven</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Trading URL"
              fullWidth
              value={values.tradingUrl || ''}
              onChange={(e) => onChange('tradingUrl', e.target.value)}
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>
    </Grid>

    {/* Group 2: Price Information */}
    <Grid item xs={12} md={6}>
      <Paper elevation={1} sx={{ p: 2, mb: 2, height: '100%' }}>
        <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
          💰 Price Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Entry Price"
              type="number"
              fullWidth
              value={values.entryPrice || ''}
              onChange={(e) => onChange('entryPrice', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Exit Price"
              type="number"
              fullWidth
              value={values.exitPrice || ''}
              onChange={(e) => onChange('exitPrice', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Stop Loss"
              type="number"
              fullWidth
              value={values.stopLoss || ''}
              onChange={(e) => onChange('stopLoss', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={`Position Size (${settings?.defaultCurrency || '$'})`}
              type="number"
              fullWidth
              value={values.positionSize || ''}
              onChange={(e) => onChange('positionSize', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Leverage"
              type="number"
              fullWidth
              value={values.leverage || 1}
              onChange={(e) => onChange('leverage', e.target.value)}
              inputProps={{ min: 1, max: 100 }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="P&L Amount"
              type="number"
              fullWidth
              value={values.profitLoss || ''}
              onChange={(e) => onChange('profitLoss', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.enableAlerts || false}
                  onChange={(e) => onChange('enableAlerts', e.target.checked)}
                />
              }
              label="🔔 Enable Price Alerts (Buy Zone, Take Profit, Stop Loss)"
            />
          </Grid>
          
          {metrics?.riskRewardRatio && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box sx={{ 
                p: 2, 
                bgcolor: 'primary.light',
                color: 'primary.contrastText',
                borderRadius: 1
              }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  📈 Risk/Reward Analysis
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      R:R Ratio
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {metrics.riskRewardRatioFormatted || '1:1'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Risk Amount
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      ${metrics.riskAmount}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Reward Amount
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      ${metrics.rewardAmount}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Quantity
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {metrics.quantity}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Grid>

    {/* Group 3: Timing Information & Strategy */}
    <Grid item xs={12} md={6}>
      <Paper elevation={1} sx={{ p: 2, mb: 2, height: '100%' }}>
        <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
          🕒 Timing & Strategy
        </Typography>
        <Grid container spacing={2}>
          {/* Timing Information */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Trade Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={values.tradeDate || ''}
              onChange={(e) => onChange('tradeDate', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Trade Time"
              type="time"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={values.tradeTime || ''}
              onChange={(e) => onChange('tradeTime', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Close Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={values.closeDate || ''}
              onChange={(e) => onChange('closeDate', e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Close Time"
              type="time"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={values.closeTime || ''}
              onChange={(e) => onChange('closeTime', e.target.value)}
              size="small"
            />
          </Grid>
          
          {/* Strategy & Setup */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
              📈 Strategy & Setup
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={strategies}
              getOptionLabel={(option) => option.name}
              value={strategies.filter(s => values.strategyIds?.includes(s.strategyId))}
              onChange={(e, newVal) => onChange('strategyIds', newVal.map(v => v.strategyId))}
              renderInput={(params) => (
                <TextField {...params} label="Strategy" fullWidth size="small" />
              )}
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Setup"
              fullWidth
              value={values.setup || ''}
              onChange={(e) => onChange('setup', e.target.value)}
              placeholder="e.g., Bull Flag, Support Bounce, Breakout"
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>
    </Grid>

    {/* Group 4: Analysis & Notes */}
    <Grid item xs={12} md={6}>
      <Paper elevation={1} sx={{ p: 2, mb: 2, height: '100%' }}>
        <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
          📝 Analysis & Notes
        </Typography>
        <TextField
          label="Insight & Analysis"
          fullWidth
          multiline
          rows={4}
          value={values.insight || ''}
          onChange={(e) => onChange('insight', e.target.value)}
          placeholder="Detailed analysis, market conditions, technical indicators, reasoning..."
          size="small"
          sx={{ mb: 2 }}
        />
        <TextField
          label="Trade Notes & Lessons"
          fullWidth
          multiline
          rows={4}
          value={values.notes || ''}
          onChange={(e) => onChange('notes', e.target.value)}
          placeholder="What was your reasoning? Any lessons learned? Post-trade reflections..."
          size="small"
        />
      </Paper>
    </Grid>

    {/* Group 5: Rating & Tags */}
    <Grid item xs={12} md={6}>
      <Paper elevation={1} sx={{ p: 2, mb: 2, height: '100%' }}>
        <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
          ⭐ Rating & Tags
        </Typography>
        <Box sx={{ mb: 3 }}>
          <Typography component="legend" sx={{ mb: 1 }}>
            Trade Rating
          </Typography>
          <Rating
            value={values.rating || 0}
            onChange={(e, newVal) => onChange('rating', newVal)}
            size="large"
          />
        </Box>
        
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Tags</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, minHeight: 40, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          {(Array.isArray(values.tags) ? values.tags : typeof values.tags === 'string' ? values.tags.split(',').map(tag => tag.trim()) : []).map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              onDelete={() => {
                const currentTags = Array.isArray(values.tags) ? values.tags : typeof values.tags === 'string' ? values.tags.split(',').map(t => t.trim()) : [];
                onChange('tags', currentTags.filter((_, i) => i !== index));
              }}
              size="small"
              color="primary"
              variant="filled"
              sx={{ borderRadius: '16px', fontWeight: 'medium' }}
            />
          ))}
        </Box>
        <Autocomplete
          freeSolo
          options={tagsToUse}
          value={''}
          onChange={(event, newValue) => {
            if (newValue) {
              const currentTags = Array.isArray(values.tags) ? values.tags : typeof values.tags === 'string' ? values.tags.split(',').map(t => t.trim()) : [];
              if (!currentTags.includes(newValue)) {
                onChange('tags', [...currentTags, newValue]);
              }
            }
          }}
          renderInput={(params) => <TextField {...params} placeholder="Type and press Enter to add tags" size="small" />}
          size="small"
        />
      </Paper>
    </Grid>
  </Grid>
));

const commonTags = [
  'Breakout', 'Reversal', 'Trend Following', 'Scalp', 'Swing',
  'Day Trade', 'Position', 'High Probability', 'Low Risk',
  'Momentum', 'Support/Resistance', 'Pattern Trade'
];

const TradeFormFields = React.memo(({ values, onChange, strategies = [], trades = [], defaultTab = 0, userTags = [] }) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const { settings } = useSettings();
  const tagsToUse = userTags.length > 0 ? userTags : commonTags;

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleStatusChange = useCallback((newStatus) => {
    const closedStatuses = ['WIN', 'LOSS', 'BREAKEVEN'];
    const now = new Date();
    if (closedStatuses.includes(newStatus)) {
      if (!values.closeDate) {
        onChange('closeDate', now.toISOString().split('T')[0]);
      }
      if (!values.closeTime) {
        onChange('closeTime', now.toTimeString().split(' ')[0].slice(0, 5));
      }
    }
    onChange('status', newStatus);
  }, [onChange, values.closeDate, values.closeTime]);

  // Format risk-reward ratio as 1:X
  const formatRiskRewardRatio = useCallback((ratio) => {
    if (!ratio || ratio <= 0) return '1:1';
    return `1:${ratio.toFixed(1)}`.replace('.0', '');
  }, []);

  const metrics = useMemo(() => {
    const { entryPrice, stopLoss, exitPrice, positionSize, leverage } = values;
    const ep = parseFloat(entryPrice);
    const sl = parseFloat(stopLoss);
    const tp = parseFloat(exitPrice);
    const size = parseFloat(positionSize);
    const lev = parseFloat(leverage);
    if (!ep || !sl || !tp || !size || !lev) return {};
    const risk = Math.abs(ep - sl);
    const reward = Math.abs(tp - ep);
    const qty = (size * lev) / ep;
    const riskAmt = risk * qty;
    const rewardAmt = reward * qty;
    const rawRatio = rewardAmt / riskAmt;
    return {
      riskAmount: riskAmt.toFixed(2),
      rewardAmount: rewardAmt.toFixed(2),
      quantity: qty.toFixed(2),
      riskRewardRatio: rawRatio.toFixed(2),
      riskRewardRatioFormatted: formatRiskRewardRatio(rawRatio)
    };
  }, [values.entryPrice, values.stopLoss, values.exitPrice, values.positionSize, values.leverage, formatRiskRewardRatio]);



  return (
    <Box>
      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2, '& .MuiTab-root': { fontWeight: 'bold', textTransform: 'none' } }}>
        <Tab label="⚡ Quick Edit" />
        <Tab label="📋 Detailed" />
      </Tabs>

      {activeTab === 0
        ? <QuickEditSection values={values} onChange={onChange} handleStatusChange={handleStatusChange} tagsToUse={tagsToUse} />
        : <DetailedSection values={values} onChange={onChange} handleStatusChange={handleStatusChange} strategies={strategies} trades={trades} metrics={metrics} settings={settings} tagsToUse={tagsToUse} />}
    </Box>
  );
});

export default TradeFormFields;
